import * as React from "react";
import { useState, useCallback } from "react";
import styled from "styled-components";
import {
  Link,
  DataTable,
  NoSearchResult,
  PaginatedList,
  QueryHelper,
  SearchForm,
  HoverForHelp
} from "components/common";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import useSearchFilterUsagesQuery from "search-filter/hooks/useSearchFilterUsagesQuery";
import { FILTER_USAGE_ENTITY_TYPE } from "search-filter/constants";
import StringUtils from "util/StringUtils";
import useShowRouteForEntity from "routing/hooks/useShowRouteForEntity";
const TABLE_HEADERS = ["Name", "Capability"];
const DEFAULT_QUERY = {
  query: "",
  per_page: 10,
  page: 1,
  sort: "title",
  order: "asc"
};
const StyledPaginatedList = styled(PaginatedList)`
  .pagination {
    margin: 0;
  }
`;
const Title = styled.div`
  display: flex;
  align-items: center;
`;
const StyledHoverForHelp = styled((props) => /* @__PURE__ */ React.createElement(HoverForHelp, { ...props }))`
  margin-left: 5px;
`;
const FilterUsage = ({ id, type, title, capability }) => {
  const currentUser = useCurrentUser();
  let hasPermissions;
  let entityRoute;
  const eventDefinitionRoute = useShowRouteForEntity(id, "EVENT_DEFINITION");
  switch (type) {
    case FILTER_USAGE_ENTITY_TYPE.EVENT_DEFINITION:
      hasPermissions = isPermitted(currentUser.permissions, `eventdefinitions:read:${id}`);
      entityRoute = eventDefinitionRoute;
      break;
    case FILTER_USAGE_ENTITY_TYPE.SIGMA_RULE:
      hasPermissions = isPermitted(currentUser.permissions, `sigma_rules:read:${id}`);
      entityRoute = "/security/sigma";
      break;
    default:
      return /* @__PURE__ */ React.createElement("div", null);
  }
  return /* @__PURE__ */ React.createElement("tr", { key: id }, /* @__PURE__ */ React.createElement("td", { className: "limited" }, /* @__PURE__ */ React.createElement(Title, null, hasPermissions ? /* @__PURE__ */ React.createElement(Link, { to: entityRoute }, title) : title, capability === "None" && /* @__PURE__ */ React.createElement(StyledHoverForHelp, { title: "Missing Permissions" }, "This ", type.toLowerCase(), " is including the search filter, but you do not have any permissions for it."))), /* @__PURE__ */ React.createElement("td", null, capability));
};
const FilterUsages = ({ filterId, type }) => {
  const [filterQuery, setSearchFilter] = useState(DEFAULT_QUERY);
  const { data } = useSearchFilterUsagesQuery(filterId, { ...filterQuery, type });
  const handlePaginationChange = useCallback(
    (page) => {
      setSearchFilter((cur) => ({ ...cur, page }));
    },
    [setSearchFilter]
  );
  const handleSearch = useCallback(
    (newSearchQuery) => setSearchFilter((cur) => ({
      ...cur,
      page: DEFAULT_QUERY.page,
      query: newSearchQuery
    })),
    []
  );
  const handleReset = useCallback(() => handleSearch(DEFAULT_QUERY.query), [handleSearch]);
  const renderOverviewItem = useCallback((entity) => /* @__PURE__ */ React.createElement(FilterUsage, { ...entity }), []);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", { className: "description" }, "Found ", data.pagination.total, " ", StringUtils.pluralize(data.pagination.total, "usage which includes", "usages which include"), " this search filter."), /* @__PURE__ */ React.createElement(
    StyledPaginatedList,
    {
      activePage: filterQuery.page,
      totalItems: data.pagination.total,
      onChange: handlePaginationChange
    },
    /* @__PURE__ */ React.createElement(
      DataTable,
      {
        className: "table-hover",
        customFilter: /* @__PURE__ */ React.createElement(
          SearchForm,
          {
            onSearch: handleSearch,
            queryHelpComponent: /* @__PURE__ */ React.createElement(QueryHelper, { entityName: "title", commonFields: ["title"] }),
            onReset: handleReset,
            topMargin: 0
          }
        ),
        dataRowFormatter: renderOverviewItem,
        noDataText: /* @__PURE__ */ React.createElement(NoSearchResult, null, "No usages have been found."),
        headers: TABLE_HEADERS,
        id: "shared-entities",
        rowClassName: "no-bm",
        rows: data.elements,
        sortByKey: "title"
      }
    )
  ));
};
export default FilterUsages;
